/* ==========================================================================
   My Teacher Says — live preview for personalised name stamps

   Makes the "Make it yours" panel (snippets/mts-stamp-preview.liquid, or for rectangular
   stamps snippets/mts-stamp-panel-linear.liquid) live: as the customer types, the text on
   the inline SVGs redraws in the stamp fonts and the chosen ink, the design's "says"
   suffix is added, long names are fitted with the shared rule in mts-stamp-layout.js,
   and the fit is written to the hidden properties that travel with the order. On desktop
   the main gallery image becomes a tile showing the same impression(s).

   A product carries one design, or, for a set, a list: one name, one preview per design.
   A set's other stamps (fixed, no name) are shown alongside so the whole set is visible;
   they only need recolouring when the ink changes.

   Three design types:
     arc2     name and phrase in one font at one size, fitted together (fitPair)
     library  fixed words in DK The Cats Whiskers; only the name (Play Day on an oval,
              or in the bottom arc) is fitted (fitSingle)
     linear   a rectangle: one or two straight lines beside an icon that is the product's
              variant (fitLines); two fields, "Line 1" and "Line 2 (optional)"

   Deferred and dependency-free. Nothing here runs before first paint; without JS the
   panel is still complete and the form still submits the typed name.
   ========================================================================== */
(function () {
  'use strict';

  var L = window.MTS_STAMP_LAYOUT;
  var panel = document.querySelector('[data-mts-stamp]');
  if (!L || !panel) return;

  var designs;
  try { designs = JSON.parse(panel.getAttribute('data-mts-stamp')); } catch (e) { return; }
  if (!Array.isArray(designs)) designs = [designs];
  if (!designs.length) return;

  var input = panel.querySelector('[data-mts-name]');
  var input2 = panel.querySelector('[data-mts-name2]');
  var clear = panel.querySelector('[data-mts-clear]');
  var count = panel.querySelector('[data-mts-count]');
  var count2 = panel.querySelector('[data-mts-count2]');
  var error = panel.querySelector('[data-mts-error]');
  var arts = panel.querySelectorAll('[data-mts-art]');
  var fixed = panel.querySelectorAll('[data-mts-fixed]');
  var fitField = panel.querySelector('[data-mts-fit]');
  var form = input && input.form;
  if (!input || !arts.length || !form) return;

  var first = designs[0];
  /* A rectangle with two typed lines (the fabric stamp) has two fields; a rectangle with a
     fixed phrase over one typed name (the Library rectangle) behaves like a round stamp. */
  var isLinear = first.type === 'linear' && first.layout !== 'fixed-name';
  var ink = panel.getAttribute('data-mts-ink') || '#111111';
  var icon = panel.getAttribute('data-mts-icon') || '';
  var PLACEHOLDER = 'Your name here';
  var FAMILY = { stamp: '"MTS Stamp", "Comic Sans MS", sans-serif', cats: '"MTS Cats", "Comic Sans MS", sans-serif', playday: '"MTS PlayDay", "Brush Script MT", cursive', loverine: '"MTS Loverine", "Brush Script MT", cursive' };
  var stampName = first.type === 'linear' ? 'the stamp' : 'a ' + (first.cut === 40 ? '35mm' : '23mm') + ' stamp';

  /* Text is measured on a canvas in the right stamp font; widths are in design units
     because the font size passed in is in design units. */
  var ctx = document.createElement('canvas').getContext('2d');
  function measurer(fontKey) {
    return function (text, fs) { ctx.font = fs + 'px ' + (FAMILY[fontKey] || FAMILY.stamp); return ctx.measureText(text).width; };
  }

  /* What the customer has typed, as it will be printed. Round stamps: one string, with the
     design's suffix ("says") always added (Liam, 3 Sept 2026; typing it yourself does not
     double it). Rectangular stamps: the two lines, trimmed, as an array. */
  function composed() {
    if (isLinear) {
      var l1 = String(input.value || '').replace(/\s+/g, ' ').trim();
      var l2 = input2 ? String(input2.value || '').replace(/\s+/g, ' ').trim() : '';
      return l1 ? (l2 ? [l1, l2] : [l1]) : [];
    }
    return L.compose(input.value, first.suffix, true);
  }
  function isLive(name) { return isLinear ? name.length > 0 : name.length > 0; }

  function setText(svg, key, text, fit, tracking, x, y) {
    var t = svg.querySelector('[data-mts-text="' + key + '"]');
    if (!t) return;
    if (fit) {
      /* Inline styles rather than attributes: theme CSS can override an SVG attribute. */
      var tr = tracking == null ? fit.tracking : tracking;
      t.style.fontSize = fit.fontSize + 'px';
      t.style.letterSpacing = (tr * fit.fontSize).toFixed(1) + 'px';
    }
    if (x != null) t.setAttribute('x', x);
    if (y != null) t.setAttribute('y', y);
    var tp = t.querySelector('textPath');
    (tp || t).textContent = text;
  }

  /* Artwork served as an external image is recoloured by a colour matrix: black to the
     ink, white left alone. Rewritten whenever the ink changes. */
  function inkMatrix(svg) {
    var m = svg.querySelector('.mts-stamp__inkmatrix');
    if (!m) return;
    var hex = /^#?([0-9a-f]{6})$/i.exec(ink.trim());
    if (!hex) return;
    var r = parseInt(hex[1].slice(0, 2), 16) / 255, g = parseInt(hex[1].slice(2, 4), 16) / 255, b = parseInt(hex[1].slice(4, 6), 16) / 255;
    m.setAttribute('values', (1 - r) + ' 0 0 0 ' + r + '  0 ' + (1 - g) + ' 0 0 ' + g + '  0 0 ' + (1 - b) + ' 0 ' + b + '  0 0 0 1 0');
  }

  /* Rectangular stamp: one or two straight lines in the column beside the icon. The column
     (left|right|preferred centre) is on the svg, set by the server for the current icon. */
  function paintLinear(svg, lines, design) {
    var col = (svg.getAttribute('data-mts-col') || '').split('|').map(Number);
    var left = col[0] || 0, right = col[1] || design.box, cxPref = col[2] || design.box / 2;
    if (design.layout === 'fixed-name') {
      /* the fixed phrase never moves; only the name is fitted, in its script face */
      var name = typeof lines === 'string' ? lines : (lines[0] || '');
      var fitN = L.fitLines([name || PLACEHOLDER], measurer(design.nameFont), { maxW: right - left, fontMax: design.nameSize });
      fitN.cx = Math.round(Math.min(Math.max(cxPref, left + fitN.width / 2), right - fitN.width / 2));
      setText(svg, 'line1', name || PLACEHOLDER, fitN, fitN.tracking, fitN.cx, design.nameBase);
      return fitN;
    }
    var shown = lines.length ? lines : ['First name', 'Surname'];
    var two = shown.length > 1;
    var fit = L.fitLines(shown, measurer(design.font || 'stamp'), { maxW: right - left, fontMax: two ? design.size2 : design.size1 });
    var cx = Math.min(Math.max(cxPref, left + fit.width / 2), right - fit.width / 2);
    fit.cx = Math.round(cx);
    var y1 = two ? design.base1 : design.boxH / 2 + design.cap * fit.fontSize / 2;
    setText(svg, 'line1', shown[0], fit, fit.tracking, fit.cx, y1);
    var t2 = svg.querySelector('[data-mts-text="line2"]');
    if (t2) {
      if (two) { setText(svg, 'line2', shown[1], fit, fit.tracking, fit.cx, design.base2); t2.removeAttribute('hidden'); }
      else { t2.textContent = ''; t2.setAttribute('hidden', ''); }
    }
    return fit;
  }

  /* Paints one SVG (a panel art, or its clone on the desktop tile). Returns the fit. */
  function paint(svg, name, design) {
    inkMatrix(svg);
    if (design.type === 'linear') return paintLinear(svg, name, design);
    var shown = name || PLACEHOLDER;
    if (design.type === 'library') {
      var nameInBottom = design.botText.indexOf('{name}') > -1;
      var fitS;
      if (nameInBottom) {
        fitS = L.fitSingle(L.arcText(shown, design.botText), measurer(design.botFont), { r: design.botR, spanMax: 170, fontMax: design.botSize, trackStart: design.botTrack, trackMin: design.botTrack - 0.03 });
        setText(svg, 'bottom', L.arcText(shown, design.botText), fitS);
      } else {
        fitS = L.fitSingle(shown, measurer(design.nameFont), { r: (design.nameRx + design.nameRy) / 2, spanMax: design.nameSpan, fontMax: design.nameSize, trackStart: design.nameTrack, trackMin: design.nameTrack - 0.03 });
        setText(svg, 'name', shown, fitS);
      }
      return fitS;
    }
    var topText = L.arcText(shown, design.top);
    var bottomText = L.arcText(shown, design.bottom);
    var limits = { rTop: design.rTop, rBottom: design.rBottom, spanTop: design.spanTop, spanBottom: design.spanBottom, fontMax: design.size };
    var fit = L.fitPair(topText, bottomText, measurer('stamp'), limits);
    setText(svg, 'top', topText, fit, fit.trackingTop);
    setText(svg, 'bottom', bottomText, fit, fit.trackingBottom);
    return fit;
  }

  /* Paints one SVG per design, in order. */
  function paintAll(svgs, name) {
    var fits = [];
    for (var i = 0; i < svgs.length && i < designs.length; i++) fits.push(paint(svgs[i], name, designs[i]));
    return fits;
  }
  function svgsIn(nodes) {
    var out = [];
    for (var i = 0; i < nodes.length; i++) { var s = nodes[i].querySelector('svg'); if (s) out.push(s); }
    return out;
  }
  function recolour(svgs) { for (var i = 0; i < svgs.length; i++) inkMatrix(svgs[i]); }

  /* "<size>|<top tracking>|<bottom tracking>" for the arcs; "<size>|<tracking>|<centre x>"
     for a rectangle. The sheet builder prints from exactly this. */
  function fitString(fit) {
    if (fit.cx != null) return fit.fontSize + '|' + fit.tracking.toFixed(3) + '|' + fit.cx;
    return fit.fontSize + '|' + (fit.trackingTop == null ? fit.tracking : fit.trackingTop).toFixed(3) + '|' + (fit.trackingBottom == null ? fit.tracking : fit.trackingBottom).toFixed(3);
  }

  function setError(message) {
    if (!error) return;
    if (message) { error.textContent = message; error.hidden = false; } else { error.hidden = true; }
  }

  function updateCounts() {
    if (count) count.textContent = input.value.length + '/' + (input.maxLength > 0 ? input.maxLength : L.MAX_CHARS);
    if (count2 && input2) count2.textContent = input2.value.length + '/' + (input2.maxLength > 0 ? input2.maxLength : L.MAX_CHARS);
  }

  var jotter = null;
  var touched = false;
  function render() {
    var name = composed();
    var live = isLive(name);
    panel.style.setProperty('--mts-ink', ink);
    recolour(svgsIn(fixed));
    /* Until the customer types, the server-rendered placeholder (pre-fitted at build time)
       is already right, so nothing is measured or fitted and no tile is built. Keeps the
       script off the main thread at load (it is not needed for first paint or LCP). */
    if (!live && !touched) {
      updateCounts();
      if (clear) clear.hidden = true;
      if (fitField) fitField.value = '';
      return;
    }
    touched = true;
    var fits = paintAll(svgsIn(arts), name);
    var allFit = true;
    for (var i = 0; i < fits.length; i++) if (!fits[i].fits) allFit = false;
    for (var a = 0; a < arts.length; a++) arts[a].classList.toggle('is-placeholder', !live);
    updateCounts();
    if (count) count.classList.toggle('is-over', !allFit);
    if (count2) count2.classList.toggle('is-over', !allFit);
    if (clear) clear.hidden = !input.value.length;
    /* one fit per design, ";" between */
    if (fitField) fitField.value = live ? fits.map(fitString).join(';') : '';
    if (!allFit) {
      var longest = isLinear ? Math.max.apply(null, name.map(function (s) { return s.length; })) : name.length;
      setError('That’s a bit long for ' + stampName + '. Try a shorter version, up to about ' + Math.max(6, Math.floor(longest * 0.8)) + ' characters' + (isLinear ? ' a line' : '') + '.');
    } else if (error && !error.hidden && error.textContent.indexOf('long') > -1) {
      setError('');
    }
    updateJotter(live, name);
    return allFit;
  }

  /* A copy of an art's SVG with its own ids (arcs and filter), so two copies on one page
     never share a textPath target. */
  function cloneSvg(svg) {
    var copy = svg.cloneNode(true);
    var paths = copy.querySelectorAll('path[id]');
    for (var i = 0; i < paths.length; i++) {
      var id = paths[i].id + '-jotter';
      var tp = copy.querySelector('textPath[href="#' + paths[i].id + '"]');
      paths[i].id = id;
      if (tp) tp.setAttribute('href', '#' + id);
    }
    var filt = copy.querySelector('filter[id]');
    if (filt) {
      var img = copy.querySelector('image[filter]');
      filt.id = filt.id + '-jotter';
      if (img) img.setAttribute('filter', 'url(#' + filt.id + ')');
    }
    return copy;
  }

  /* Desktop gallery tile: the impression(s) on a jotter page (or, for the fabric stamp, a
     name label), in the chosen ink, with a set's other stamps along the bottom. Built
     lazily, inside the stage, so the variant swap (which replaces the stage) simply drops
     it and it is rebuilt on the next render. Hidden on mobile by CSS. */
  var jotterMain = null, jotterRest = null;
  function dropJotter() {
    if (jotter && jotter.parentNode) jotter.parentNode.removeChild(jotter);
    jotter = null; jotterMain = null; jotterRest = null;
  }
  function updateJotter(live, name) {
    var stage = document.querySelector('.mts-pdp__stage');
    if (!stage) return;
    if (!jotter || !stage.contains(jotter)) {
      jotter = document.createElement('div');
      jotter.className = 'mts-stamp__jotter' + (arts.length > 1 || fixed.length ? ' mts-stamp__jotter--set' : '') + (isLinear ? ' mts-stamp__jotter--linear' : '');
      jotter.setAttribute('aria-hidden', 'true');
      jotterMain = document.createElement('div');
      jotterMain.className = 'mts-stamp__jotter-main';
      var i;
      for (i = 0; i < arts.length; i++) jotterMain.appendChild(cloneSvg(arts[i].querySelector('svg')));
      jotter.appendChild(jotterMain);
      jotterRest = null;
      if (fixed.length) {
        jotterRest = document.createElement('div');
        jotterRest.className = 'mts-stamp__jotter-rest';
        for (i = 0; i < fixed.length; i++) jotterRest.appendChild(cloneSvg(fixed[i].querySelector('svg')));
        jotter.appendChild(jotterRest);
      }
      stage.appendChild(jotter);
    }
    jotter.style.setProperty('--mts-ink', ink);
    /* Direct children only: the inline icon inside each impression is a nested <svg>. */
    paintAll(jotterMain.children, name);
    if (jotterRest) recolour(jotterRest.children);
    stage.classList.toggle('is-stamp-live', live);
  }

  input.addEventListener('input', render);
  if (input2) input2.addEventListener('input', render);
  if (clear) clear.addEventListener('click', function () { input.value = ''; render(); input.focus(); });

  /* The form is a plain POST to /cart/add. The typed name becomes the printed text
     ("Mrs McKie" -> "Mrs McKie says") only at the moment of submission, so the field
     never changes under the customer's fingers. */
  form.addEventListener('submit', function (event) {
    var name = composed();
    if (!isLive(name)) {
      event.preventDefault();
      setError('Please add the name you’d like on your stamp.');
      input.focus();
      return;
    }
    touched = true;
    if (!render()) { event.preventDefault(); input.focus(); return; }
    setError('');
    if (isLinear) { input.value = name[0]; if (input2) input2.value = name[1] || ''; }
    else input.value = name;
  });

  /* Ink swatch or icon tile chosen: mts-custom.js swaps the stage and price and then
     announces the new variant with its ink colour, its icon key and the freshly fetched
     section. A new icon's artwork is lifted from that section into the panel's art. */
  document.addEventListener('mts:variant', function (event) {
    var d = event.detail || {};
    if (d.ink) ink = d.ink;
    if (d.icon != null && d.icon !== icon) {
      icon = d.icon;
      var freshArt = d.fresh && d.fresh.querySelector ? d.fresh.querySelector('[data-mts-art] svg') : null;
      var art = arts[0] && arts[0].querySelector('svg');
      if (freshArt && art) {
        art.parentNode.replaceChild(freshArt.cloneNode(true), art);
        dropJotter();
      }
    }
    render();
  });

  render();
  /* Re-measure once the stamp fonts are in, but only if the customer has typed something
     by then (the placeholder does not need it). */
  if (document.fonts && document.fonts.load) {
    var faces = [];
    for (var d = 0; d < designs.length; d++) {
      if (designs[d].type === 'library') { faces.push('100px "MTS Cats"'); faces.push('100px "MTS PlayDay"'); }
      else if (designs[d].nameFont === 'loverine') { faces.push('100px "MTS Stamp"'); faces.push('100px "MTS Loverine"'); }
      else faces.push('100px "MTS Stamp"');
    }
    for (var f = 0; f < faces.length; f++) document.fonts.load(faces[f]).then(function () { if (touched) render(); }, function () {});
  }
})();
